import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import * as sessionApi from '@/api/session';
import { ApiError } from '@/lib/api';
import type { Session } from '@/types';

/**
 * Has this browser ever completed a session fetch? The session cookie itself
 * is httpOnly, so before the first /api/session answer the SPA cannot tell a
 * customer from a visitor — and the two want different loading states. A
 * customer should see the app's skeleton; a visitor who is about to be sent
 * to /landing should see nothing at all, because the app's sidebar taking
 * shape and then vanishing is the flash this exists to remove.
 *
 * Written on a successful fetch, cleared on a 401 or an explicit sign-out.
 * The server's own check on `/` (apps/server/src/routes/root.ts) handles the
 * front door before any of this runs; the hint covers deep links and dev.
 */
const SEEN_SESSION_KEY = 'billable.seen-session';

export function hasSeenSession(): boolean {
  try {
    return localStorage.getItem(SEEN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberSession(seen: boolean) {
  try {
    if (seen) localStorage.setItem(SEEN_SESSION_KEY, '1');
    else localStorage.removeItem(SEEN_SESSION_KEY);
  } catch {
    // Storage unavailable; the next load simply gets the neutral state.
  }
}

export function useSessionQuery() {
  return useQuery({
    queryKey: qk.session,
    queryFn: async ({ signal }) => {
      try {
        const session = await sessionApi.fetchSession(signal);
        rememberSession(true);
        return session;
      } catch (error) {
        if (error instanceof ApiError && error.isAuth) rememberSession(false);
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000
  });
}

/**
 * Session is provided rather than re-queried per component so screens can rely
 * on it being present (the auth gate guarantees it) instead of handling a
 * `undefined` user everywhere.
 */
export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession must be used inside an authenticated route.');
  }
  return session;
}

/**
 * `case_based` teams (law firms) bill a separate payer and call the matter a
 * "Client"; `direct` teams bill the client and call it a "Project". Every
 * user-facing noun goes through here.
 */
export function useBillingNouns() {
  const session = useContext(SessionContext);
  const direct = session?.team?.billingType === 'direct';
  return {
    client: direct ? 'Project' : 'Client',
    clientLower: direct ? 'project' : 'client',
    clients: direct ? 'Projects' : 'Clients',
    payer: direct ? 'Billing contact' : 'Billing party',
    payers: direct ? 'Billing contacts' : 'Billing parties'
  };
}

export function useSwitchTeam() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: sessionApi.setActiveTeam,
    async onSuccess() {
      // Everything is team-scoped. Leave the current screen first — an invoice
      // id from the old team is a 404 on the new one — then forget the other
      // team's cached data and refetch whatever is still mounted, the session
      // included, so the provider re-renders with the new team.
      //
      // Not `qc.clear()`: that empties the cache while the screens keep the
      // query objects they already hold, so nothing refetches and the old team
      // stays on screen until a reload. Invalidating after a clear invalidates
      // nothing, because there is nothing left to invalidate.
      navigate('/');
      qc.removeQueries({ type: 'inactive' });
      await qc.invalidateQueries();
    },
    onError() {
      toast.error('Could not switch teams.');
    }
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sessionApi.signOut,
    onSuccess() {
      rememberSession(false);
      qc.clear();
      window.location.href = '/';
    }
  });
}
